import { useCallback, useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/view/components/ui/dialog";
import { Button } from "@/view/components/ui/button";
import { useTable } from "@/app/hooks/useTable";
import { selfHireService } from "@/app/services/selfHireService";
import { useToast } from "@/view/components/ui/use-toast";
import debounce from "lodash.debounce";
import { twMerge } from "tailwind-merge";

interface CreateLinkModalProps {
  visible: boolean;
  newLinkName: string;
  setNewLinkName: (name: string) => void;
  selectedTable: string;
  setSelectedTable: (table: string) => void;
  onCreateLink: () => void;
  isLoading: boolean;
  onClose: () => void;
}

const CreateLinkModal = ({
  visible,
  newLinkName,
  setNewLinkName,
  selectedTable,
  setSelectedTable,
  onCreateLink,
  isLoading,
  onClose,
}: CreateLinkModalProps) => {
  const {
    data: tables,
    isLoading: isLoadingTables,
    setParams,
    fetchNextPage,
    isFetchingNextPage,
  } = useTable();
  const listRef = useRef<HTMLDivElement>(null);
  const [isFetching, setIsFetching] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [isNameValid, setIsNameValid] = useState<boolean | null>(null);
  const [isCheckingName, setIsCheckingName] = useState(false);

  const { toast } = useToast();

  useEffect(() => {
    setParams({ isActive: "true" });
  }, [setParams]);

  useEffect(() => {
    if (!visible) {
      setInputValue("");
      setIsNameValid(null);
      setIsCheckingName(false);
    }
  }, [visible]);

  const handleScroll = () => {
    if (listRef.current && tables?.pages && !isFetching) {
      const { scrollTop, scrollHeight, clientHeight } = listRef.current;
      if (scrollTop + clientHeight >= scrollHeight - 5) {
        const lastPage = tables.pages[tables.pages.length - 1];
        if (lastPage && lastPage.tables.length > 0) {
          setIsFetching(true);
          fetchNextPage().finally(() => {
            setIsFetching(false);
          });
        }
      }
    }
  };

  const checkNameAvailability = useCallback(
    async (name: string) => {
      if (!name.trim()) {
        setIsNameValid(null);
        return;
      }

      setIsCheckingName(true);
      try {
        const isAvailable = await selfHireService.checkName(name.trim());
        setIsNameValid(isAvailable);

        if (isAvailable) {
          setNewLinkName(name.trim());
        } else {
          toast({
            variant: "destructive",
            title: "Nome já está em uso",
            description: "Escolha outro nome para o link.",
          });
        }
      } catch (error: any) {
        console.error("Erro ao verificar nome:", error);
        toast({
          variant: "destructive",
          title: "Erro ao verificar nome",
          description:
            error?.message || "Erro ao verificar disponibilidade do nome",
        });
        setIsNameValid(false);
      } finally {
        setIsCheckingName(false);
      }
    },
    [setNewLinkName, toast],
  );

  const debouncedCheckName = useRef(
    debounce(checkNameAvailability, 2000),
  ).current;

  const handleNameChange = (text: string) => {
    setInputValue(text);
    setIsNameValid(null);

    if (text.trim()) {
      debouncedCheckName(text);
    } else {
      setIsNameValid(null);
      setNewLinkName("");
    }
  };

  const isButtonDisabled =
    !newLinkName.trim() || !isNameValid || isCheckingName || isLoading;

  return (
    <Dialog open={visible} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Criar link de autocontratação</DialogTitle>
          <DialogDescription>
            Use esse link para divulgar e acompanhar seus resultados
          </DialogDescription>
        </DialogHeader>
        <div className="mb-4">
          <p>Nome</p>
          <input
            type="text"
            value={inputValue}
            onChange={(e) => handleNameChange(e.target.value)}
            placeholder="Defina um nome para seu link"
            className={twMerge(
              "mb-4 w-full rounded-lg border border-gray01 p-2",
              isNameValid && "border-success",
            )}
          />
          {isNameValid === false && !isCheckingName && (
            <p className="mb-4 text-xs font-medium text-error">
              Nome já está em uso
            </p>
          )}
          <div
            className="mb-4"
            ref={listRef}
            onScroll={handleScroll}
            style={{ maxHeight: "200px", overflowY: "auto" }}
          >
            <p>Selecione a tabela</p>
            {isLoadingTables ? (
              <p>Carregando tabelas...</p>
            ) : (
              <>
                {tables?.pages.map((page) =>
                  page.tables.map(
                    (table: {
                      _id: string;
                      name: string;
                      percents: number;
                      isActive: boolean;
                    }) =>
                      table.isActive && (
                        <div
                          key={table._id}
                          className="mb-2 rounded-lg border p-2"
                        >
                          <label className="flex items-center">
                            <input
                              type="radio"
                              value={table._id}
                              checked={selectedTable === table._id}
                              onChange={() => setSelectedTable(table._id)}
                              className="mr-2"
                            />
                            <div>
                              <p className="font-semibold">{table.name}</p>
                              {/* <p className="text-sm text-gray-500">Comissão: {table.percents}%</p> */}
                            </div>
                          </label>
                        </div>
                      ),
                  ),
                )}
                {isFetchingNextPage && <p>Carregando mais tabelas...</p>}
              </>
            )}
          </div>
          <Button
            onClick={onCreateLink}
            disabled={isButtonDisabled}
            className="mt-4 w-full rounded-full bg-primary text-white"
          >
            {isLoading ? "Criando link..." : "Criar link auto contratação"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CreateLinkModal;
